import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AuthCallbackPage.css';

const AuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleGoogleCallback, handleVKCallback } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      // Check for error from backend
      const error = searchParams.get('error');
      const message = searchParams.get('message');
      
      if (error) {
        setStatus('error');
        setErrorMessage(getErrorMessage(error, message));
        return;
      }

      // Get tokens from URL
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');

      if (!accessToken || !refreshToken) {
        setStatus('error');
        setErrorMessage('Не удалось получить данные авторизации');
        return;
      }

      // Handle the callback (works for both Google and VK)
      // Both use the same token format, so we can use either handler
      const result = await handleGoogleCallback(accessToken, refreshToken);
      
      if (result.success) {
        setStatus('success');
        // Redirect to orders after short delay
        setTimeout(() => {
          navigate('/orders', { replace: true });
        }, 1000);
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Ошибка при авторизации');
      }
    };

    processCallback();
  }, [searchParams, handleGoogleCallback, navigate]);

  const getErrorMessage = (error, message) => {
    switch (error) {
      case 'google_auth_failed':
        return message || 'Ошибка авторизации Google';
      case 'vk_auth_failed':
        return message || 'Ошибка авторизации VK';
      case 'no_code':
        return 'Не получен код авторизации';
      case 'oauth_not_configured':
        return 'OAuth не настроен на сервере';
      case 'token_exchange_failed':
        return 'Ошибка обмена токенов';
      case 'userinfo_failed':
        return 'Не удалось получить данные пользователя';
      case 'no_google_id':
        return 'Не удалось получить ID Google аккаунта';
      case 'no_vk_data':
        return 'Не удалось получить данные VK аккаунта';
      case 'vk_api_error':
        return message || 'Ошибка VK API';
      case 'no_user_data':
        return 'Не удалось получить данные пользователя из VK';
      case 'internal_error':
        return message || 'Внутренняя ошибка сервера';
      default:
        return message || 'Произошла ошибка при авторизации';
    }
  };

  const handleRetry = () => {
    navigate('/auth', { replace: true });
  };

  return (
    <div className="auth-callback">
      <div className="auth-callback__container">
        {status === 'processing' && (
          <>
            <div className="auth-callback__spinner" />
            <h2 className="auth-callback__title">Выполняется вход...</h2>
            <p className="auth-callback__text">Пожалуйста, подождите</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="auth-callback__icon auth-callback__icon--success">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M40 14L18 36L8 26" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="auth-callback__title">Успешный вход!</h2>
            <p className="auth-callback__text">Переход в личный кабинет...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="auth-callback__icon auth-callback__icon--error">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M36 12L12 36M12 12L36 36" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="auth-callback__title">Ошибка авторизации</h2>
            <p className="auth-callback__text">{errorMessage}</p>
            <button className="auth-callback__button" onClick={handleRetry}>
              Попробовать снова
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;

